/* eslint-disable @typescript-eslint/naming-convention -- HTTP header names */
import type { OidcAccessToken } from '@ori/auth';
import type { AxiosRequestConfig } from 'axios';
import axios from 'axios';

import { getAuthorizationToken } from './getAuthorizationToken';
import { isDataMockup } from './isDataMockup';
import { normalizeSlashChars } from './normalizeSlashChars';
import { HttpMethod } from '~/models';

export interface GetUrlOptions {
  basePath: string;
  currentLanguage: string;
  host: string;
  identityUrl?: string;
  isAuthorizationRequired?: boolean;
  isCustomerAnonymous: boolean;
  query?: string;
  requestConfig?: AxiosRequestConfig;
  tenant: string;
}

export async function getUrl<T>({
  basePath: originalBasePath,
  currentLanguage,
  host: originalHost,
  identityUrl,
  isAuthorizationRequired,
  isCustomerAnonymous,
  method: originalMethod,
  query,
  tenant,
  ...config
}: AxiosRequestConfig & GetUrlOptions): Promise<T> {
  let isMockup = false;
  if (process.env.NODE_ENV !== 'production') {
    isMockup = isDataMockup();
  }
  let method = originalMethod;
  let token: OidcAccessToken | undefined;
  const basePath = normalizeSlashChars(originalBasePath, true, false);
  const host = normalizeSlashChars(originalHost, undefined, false);
  let url = `${host}${basePath}${query ?? ''}`;

  if (isMockup) {
    let mockupBasePath = basePath;

    if (basePath.includes('shoppingSessions')) {
      mockupBasePath = basePath.replace(/shoppingSessions\/.*\//, 'shoppingSessions/');
    }

    method = HttpMethod.Get;
    url = `/mockups${mockupBasePath}.json`;
  }

  if (!isMockup && isAuthorizationRequired) {
    token = await getAuthorizationToken(
      {
        identityUrl,
        tenant,
      },
      isCustomerAnonymous,
    );
  }

  const headers = {
    ...config.headers,
    ...(token && {
      Authorization: token.toString(),
    }),
    'Accept-Language': currentLanguage,
    'x-tenant-context': tenant,
  };
  const { data } = await axios.request<T>({
    ...config,
    headers,
    method,
    url,
  });

  return data;
}
